import { createContext, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ShopError, getCatalogue, getShop, openSession } from './api';
import { catalogueStore, sessionStore, type StoredSession } from './store';
import type { Catalogue, PublicShop } from './types';

// One load per visit (design §05): shop → session → catalogue. A link from the
// chat carries ?c=<contact token>; it is exchanged for a session once, stored,
// and stripped from the address bar so a forwarded link does not carry the
// customer's identity.

export interface ShopState {
  slug: string;
  shop: PublicShop;
  session: StoredSession;
  catalogue: Catalogue;
  refresh: () => void;
}

type Loading =
  | { status: 'loading' }
  | { status: 'ready'; state: ShopState }
  | { status: 'error'; code: string; message: string };

const Ctx = createContext<ShopState | null>(null);

export function useShop(): ShopState {
  const s = useContext(Ctx);
  if (!s) throw new Error('useShop outside <ShopProvider>');
  return s;
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<Loading>({ status: 'loading' });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const token = params.get('c');

    (async () => {
      try {
        let session = sessionStore.get(slug);
        const shopRes = await getShop(slug, session?.value ?? null);
        const shop = shopRes.shop;

        // A token always wins over whatever is stored: the chat link is the
        // customer saying "this is me". Otherwise a missing or expired session
        // is replaced by an anonymous one in the default group.
        if (token || !session) {
          const s = await openSession(slug, session?.value ?? null, token);
          session = {
            value: s.session,
            contactId: s.contact?.id ?? null,
            contactName: s.contact?.name ?? null,
            groupId: s.groupId,
            pricesShown: s.pricesShown,
            expiresAt: s.expiresAt,
          };
          sessionStore.set(slug, session);
          if (token) {
            const next = new URLSearchParams(params);
            next.delete('c');
            setParams(next, { replace: true });
          }
        }

        const cached = catalogueStore.get(slug, session.groupId);
        const result = await getCatalogue(slug, session.value, session.groupId, shop.catalogueVersion, cached);
        if (!result.fromCache && result.etag) catalogueStore.set(slug, session.groupId, { etag: result.etag, catalogue: result.catalogue });

        if (!cancelled) {
          setState({
            status: 'ready',
            state: { slug, shop, session, catalogue: result.catalogue, refresh: () => setTick((t) => t + 1) },
          });
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ShopError && e.code === 'group_mismatch') {
          // The customer's group changed since the session was stored: drop it
          // and go round once more with a fresh session.
          sessionStore.set(slug, null);
          setTick((t) => t + 1);
          return;
        }
        if (e instanceof ShopError && e.code === 'token_invalid') {
          // An expired link still opens the shop — anonymously.
          const next = new URLSearchParams(params);
          next.delete('c');
          setParams(next, { replace: true });
          setTick((t) => t + 1);
          return;
        }
        const code = e instanceof ShopError ? e.code : 'network';
        const message = e instanceof ShopError ? e.message : 'Could not reach the shop. Check your connection and try again.';
        setState({ status: 'error', code, message });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `params` is read once per load on purpose; stripping ?c must not reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tick]);

  const brand = state.status === 'ready' ? state.state.shop.brandColor : '#15803d';
  const style = useMemo(() => ({ '--brand': brand }) as CSSProperties, [brand]);

  useEffect(() => {
    if (state.status === 'ready') document.title = state.state.shop.name;
  }, [state]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-dvh grid place-items-center text-stone-500" style={style}>
        <div className="flex items-center gap-3">
          <span className="size-5 rounded-full border-2 border-stone-300 border-t-[var(--brand)] animate-spin" />
          Opening the shop…
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    const notFound = state.code === 'shop_not_found';
    return (
      <div className="min-h-dvh grid place-items-center p-6 text-center">
        <div className="max-w-sm">
          <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">{notFound ? 'There is no shop here' : 'Could not open the shop'}</h1>
          <p className="mt-2 text-stone-500">{notFound ? 'Check the link you were sent.' : state.message}</p>
          {!notFound && (
            <button type="button" onClick={() => setTick((t) => t + 1)} className="mt-5 rounded-full bg-stone-900 px-5 py-2.5 text-white dark:bg-stone-100 dark:text-stone-900">
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Ctx.Provider value={state.state}>
      <div className="min-h-dvh" style={style}>
        {children}
      </div>
    </Ctx.Provider>
  );
}
